import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import {
    CommandContribution,
    CommandRegistry,
    createDialog,
} from '@patsnap/tahiti-core';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';
import {
    resolveUsageLimitUpgradeContext,
    type UsageLimitUpgradeContextOptions,
} from '../../utils/usage-limit-upgrade';

/**
 * 打开用量限制弹窗的参数
 */
export interface OpenUsageLimitModalPayload extends UsageLimitUpgradeContextOptions {
    /** 弹窗标题 */
    title?: string;
    /** 弹窗描述 */
    description?: string;
    /** 权益列表 */
    list?: string[];
    /** 升级按钮文案 */
    buttonText?: string;
    /** 自定义升级链接 */
    upgradeUrl?: string;
    /** 是否显示关闭按钮 */
    showClose?: boolean;
    /** 是否允许 ESC 关闭 */
    closeOnPressEscape?: boolean;
    /** 自定义弹窗 class */
    customClass?: string;
}

/**
 * 用量限制弹窗关闭结果
 */
export interface UsageLimitModalResult {
    /** 用户动作 */
    action: 'upgrade' | 'close';
    /** 升级跳转地址 */
    url?: string;
    /** 底层弹窗关闭动作 */
    closeAction?: string;
}

const usageLimitModalDialogServiceFactory = createDialog(() => import('../../components/UsageLimitCommandDialog.vue'));

/**
 * 用量限制弹窗命令处理器
 */
@injectable()
export class UsageLimitModalCommandHandler implements CommandContribution {
    private _dialogService = usageLimitModalDialogServiceFactory();

    constructor(
        @inject(RavenService)
        private readonly _ravenService: RavenService,
    ) {}

    private async _handleOpenUsageLimitModal(payload: OpenUsageLimitModalPayload = {}): Promise<UsageLimitModalResult> {
        return new Promise((resolve, reject) => {
            const dialog = this._dialogService;

            dialog.onSubmit(async (result, next) => {
                try {
                    if (result.action === 'upgrade') {
                        this._trackUpgrade(payload);
                    }
                    resolve(result);
                    next();
                } catch (error) {
                    next(false);
                    reject(error);
                }
            });

            dialog.onCancel(() => {
                resolve({
                    action: 'close',
                });
            });

            this._trackShow(payload);
            dialog.open(payload);
        });
    }

    private _trackShow(payload: OpenUsageLimitModalPayload) {
        const context = resolveUsageLimitUpgradeContext(payload);

        this._ravenService.track(TrackEvents.AIR_USAGE_UPGRADE_SHOW, {
            eventType: TRACK_EVENT_TYPE.EurekaUpgradePage,
            eventDetailStr: {
                ...context,
                use_where: 'ip',
            },
        });
    }

    private _trackUpgrade(payload: OpenUsageLimitModalPayload) {
        const context = resolveUsageLimitUpgradeContext(payload);

        this._ravenService.track(TrackEvents.TRACK_EVENT_UPGRADE_TRIGGER, {
            eventType: TRACK_EVENT_TYPE.EurekaUpgradePage,
            eventDetailStr: {
                ...context,
            },
        });
    }

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand({
            id: 'usage-limit:open-modal',
        }, {
            execute: (payload?: OpenUsageLimitModalPayload) => {
                return this._handleOpenUsageLimitModal(payload);
            },
        });
    }
}

declare module '@patsnap/tahiti-core' {
    export interface CommandService {
        /**
         * 打开用量限制弹窗
         * @param commandId - 命令 ID
         * @param params - 弹窗参数
         */
        executeCommand(commandId: 'usage-limit:open-modal', params?: OpenUsageLimitModalPayload): Promise<UsageLimitModalResult>;
    }
}
